import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { MarketingChrome, MarketingHashLink, scrollToId } from "./MarketingChrome";
import { PilotInquiryForm } from "./PilotInquiryForm";
import { FLOW_ACCENT, FLOW_IDS } from "./marketingFlows";
import { SCFORH_INFO_URL } from "../../constants/scforhClubHealth";
import hallPlate from "../../components/assets/landing_bg_img.jpg";
import "./MarketingHome.css";

function LiveHallMock() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("all");

  const tables = [
    {
      id: "1",
      agent: "Pingwi",
      flowId: "pingwi",
      label: "Table 1 · Juniors",
      exercise: "Target Fun & Motor Reflex",
      hr: "124",
      zone: "Moderate",
      accent: "#FFA451",
    },
    {
      id: "2",
      agent: "Pingflo",
      flowId: "pingflo",
      label: "Table 2 · Active Youth",
      exercise: "Rhythm Rally & Flow",
      hr: "138",
      zone: "Aerobic",
      accent: "#2A9BB5",
    },
    {
      id: "3",
      agent: "Pingpro",
      flowId: "pingpro",
      label: "Table 3 · League (2-3 sess/wk)",
      exercise: "Pivot Topspin Attack",
      hr: "159",
      zone: "Threshold",
      accent: "#0B1F33",
    },
    {
      id: "4",
      agent: "Pingmynd",
      flowId: "pingmynd",
      label: "Table 4 · Seniors & HEPA",
      exercise: "Cognitive Balance & Placement",
      hr: "114",
      zone: "Longevity",
      accent: "#0F7B6C",
    },
  ];

  return (
    <div className="mh-hall" aria-label="Interactive Live Hall Training Simulator">
      <img className="mh-hall__plate" src={hallPlate} alt="" />
      <div className="mh-hall__mock">
        <div className="mh-hall__bar">
          <div className="mh-hall__live-tag">
            <span className="mh-hall__beacon" />
            <strong>{t("mhHallLive", "Live session")}</strong>
          </div>
          <span className="mh-hall__mode-chip">{t("mhProofKot", "King of the Table")} · 4 min</span>
        </div>

        <div className="mh-hall__grid">
          {tables.map((row) => (
            <div
              className={`mh-table ${activeTab === row.id ? "is-focused" : ""}`}
              key={row.id}
              onClick={() => setActiveTab(row.id === activeTab ? "all" : row.id)}
              style={{ "--table-accent": row.accent }}
            >
              <div className="mh-table__top">
                <strong>{row.label}</strong>
                <span className="mh-table__agent-tag" style={{ color: row.accent }}>
                  {row.agent}
                </span>
              </div>
              <p className="mh-table__ex">{row.exercise}</p>
              <div className="mh-table__hr-row">
                <span className="mh-table__hr-val">
                  <span className="mh-table__pulse-dot" /> HR {row.hr} bpm
                </span>
                <span className="mh-table__zone">{row.zone}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="mh-hall__coach-hud">
          <span className="mh-hall__coach-badge">Coach in Control</span>
          <span className="mh-hall__subtext">One coach · 4 active tables · Auto rotations</span>
        </div>
      </div>
    </div>
  );
}

export default function MarketingHome() {
  const { t } = useTranslation();

  useEffect(() => {
    const id = String(window.location.hash || "").replace("#", "");
    if (!id) return;
    scrollToId(id);
  }, []);

  return (
    <MarketingChrome>
      <main id="mh-main">
        {/* HERO SECTION */}
        <section className="mh-wrap mh-hero">
          <div>
            <p className="mh-eyebrow">{t("mhHeroCategory")}</p>
            <h1>{t("mhHeroHeadline")}</h1>
            <p className="mh-lead">{t("mhHeroSubhead")}</p>
            <div className="mh-hero__ctas">
              <Link to="/register" className="mh-btn mh-btn--primary">
                {t("mhSignUp", "Sign up")}
              </Link>
              <Link to="/login" className="mh-btn mh-btn--ghost">
                {t("mhSignIn")}
              </Link>
              <MarketingHashLink hashId="pilot" className="mh-btn mh-btn--ghost">
                {t("mhRequestPilot")}
              </MarketingHashLink>
            </div>
            <p className="mh-micro">{t("mhHeroMicro")}</p>
            <span className="mh-chip">{t("mhHeroChip")}</span>
          </div>
          <LiveHallMock />
        </section>

        {/* PROOF TICKER */}
        <div className="mh-wrap mh-proof">
          {["mhProofLive", "mhProofHr", "mhProofKot", "mhProofScforh", "mhProofWhiteLabel", "mhProofLangs"].map(
            (key) => (
              <span key={key}>{t(key)}</span>
            )
          )}
        </div>

        {/* THE REALITY / PROBLEM SECTION */}
        <section className="mh-section" id="problem">
          <div className="mh-wrap mh-grid-2">
            <div>
              <p className="mh-eyebrow">{t("mhProblemEyebrow")}</p>
              <h2>{t("mhProblemTitle")}</h2>
            </div>
            <div>
              <p className="mh-lead">{t("mhProblemBody")}</p>
              <div className="mh-problem-cards">
                <div className="mh-problem-card">
                  <strong>❌ Membership software</strong>
                  <p>Collects annual dues and tracks attendance, but is completely absent when balls start bouncing.</p>
                </div>
                <div className="mh-problem-card">
                  <strong>❌ Post-match video apps</strong>
                  <p>Analyze isolated tournament clips after the weekend, but cannot run 4 tables live on Tuesday night.</p>
                </div>
                <div className="mh-problem-card mh-problem-card--stt">
                  <strong>✅ SmartTTClub</strong>
                  <p>Runs the multi-table session live, proves health impact, and links coach-validated SMART goals.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS: 4-WEEK SMART CYCLE */}
        <section className="mh-section" id="how" style={{ paddingTop: 0 }}>
          <div className="mh-wrap">
            <p className="mh-eyebrow">{t("mhHowEyebrow")}</p>
            <h2>{t("mhHowTitle")}</h2>
            <div className="mh-beats">
              {[1, 2, 3, 4].map((n) => (
                <article className="mh-beat" key={n}>
                  <span className="mh-beat__n">0{n}</span>
                  <h3>{t(`mhHow${n}Title`)}</h3>
                  <p>{t(`mhHow${n}Body`)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* IN THE HALL: PRODUCT FEATURES */}
        <section className="mh-section mh-section--navy" id="product">
          <div className="mh-wrap">
            <p className="mh-eyebrow">{t("mhProductEyebrow")}</p>
            <h2>{t("mhProductTitle")}</h2>
            <div className="mh-cards-3">
              <article className="mh-card">
                <h3>{t("mhProductLiveTitle")}</h3>
                <p>{t("mhProductLiveBody")}</p>
              </article>
              <article className="mh-card">
                <h3>{t("mhProductKotTitle")}</h3>
                <p>{t("mhProductKotBody")}</p>
              </article>
              <article className="mh-card">
                <h3>{t("mhProductBookTitle")}</h3>
                <p>{t("mhProductBookBody")}</p>
              </article>
            </div>
            <p className="mh-note">{t("mhProductMeasureNote")}</p>
          </div>
        </section>

        {/* PINGFLO & THE SMART AGENTS */}
        <section className="mh-section" id="pingflo">
          {/* Legacy anchor target to preserve old links to #digiping */}
          <span id="digiping" style={{ display: "block", position: "relative", top: "-100px", visibility: "hidden" }} />
          <div className="mh-wrap">
            <p className="mh-eyebrow">{t("mhDigiEyebrow", "Pingflo & Agents")}</p>
            <h2>{t("mhDigiTitle", "Meet Pingflo: The club’s SMART host & orchestrator")}</h2>
            <p className="mh-lead">{t("mhDigiLead")}</p>
            <p className="mh-note">{t("mhDigiAiNote")}</p>
            <div className="mh-roles">
              <article className="mh-card">
                <h3>{t("mhDigiAdminTitle")}</h3>
                <p>{t("mhDigiAdminBody")}</p>
              </article>
              <article className="mh-card">
                <h3>{t("mhDigiCoachTitle")}</h3>
                <p>{t("mhDigiCoachBody")}</p>
              </article>
              <article className="mh-card">
                <h3>{t("mhDigiPlayerTitle")}</h3>
                <p>{t("mhDigiPlayerBody")}</p>
              </article>
            </div>
            <p className="mh-note">{t("mhDigiConductors")}</p>
            <p className="mh-lead" style={{ marginTop: 24 }}>
              {t("mhDigiBridge")}
            </p>
          </div>
        </section>

        {/* THE FOUR FLOWS */}
        <section className="mh-section" id="flows" style={{ paddingTop: 0 }}>
          <div className="mh-wrap">
            <p className="mh-eyebrow">{t("mhFlowsEyebrow")}</p>
            <h2>{t("mhFlowsTitle")}</h2>
            <p className="mh-lead">{t("mhFlowsLead")}</p>
            <div className="mh-flow-board">
              {FLOW_IDS.map((id) => (
                <Link
                  key={id}
                  to={`/flows/${id}`}
                  className="mh-flow-card"
                  style={{ "--flow-accent": FLOW_ACCENT[id] }}
                >
                  <div className="mh-flow-card__header">
                    <h3>{t(`mhFlow_${id}_name`)}</h3>
                    {t(`mhFlow_${id}_badge`) ? (
                      <span className="mh-flow-card__badge">{t(`mhFlow_${id}_badge`)}</span>
                    ) : null}
                  </div>
                  <p className="mh-flow-card__mission">{t(`mhFlow_${id}_mission`)}</p>

                  {id === "pingpro" && t("mhFlow_pingpro_commitment") ? (
                    <div className="mh-flow-card__commitment">
                      <strong>⚡ {t("mhFlow_pingpro_commitment")}</strong>
                    </div>
                  ) : null}

                  <ul className="mh-kws">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <li key={n}>{t(`mhFlow_${id}_k${n}`)}</li>
                    ))}
                  </ul>
                  <span className="mh-flow-link">
                    {t("mhFlowMeet", { name: t(`mhFlow_${id}_name`) })} →
                  </span>
                </Link>
              ))}
            </div>
            <p className="mh-fork">{t("mhFlowsForkNote")}</p>
          </div>
        </section>

        {/* SCFORH & ONE HEALTH SECTION */}
        <section className="mh-section" id="method">
          <div className="mh-wrap">
            <p className="mh-eyebrow">{t("mhMethodEyebrow")}</p>
            <h2>{t("mhMethodTitle")}</h2>
            <p className="mh-lead">{t("mhMethodBody")}</p>

            {/* 3 Pillars of Health */}
            <div className="mh-scforh-pillars">
              <article className="mh-scforh-pillar">
                <div className="mh-scforh-pillar__icon">🏃</div>
                <h3>{t("mhScforhPillarPhysicalTitle", "Physical Resilience")}</h3>
                <p>{t("mhScforhPillarPhysicalBody")}</p>
              </article>
              <article className="mh-scforh-pillar">
                <div className="mh-scforh-pillar__icon">🧠</div>
                <h3>{t("mhScforhPillarMentalTitle", "Cognitive Vitality ('Aerobic Chess')")}</h3>
                <p>{t("mhScforhPillarMentalBody")}</p>
              </article>
              <article className="mh-scforh-pillar">
                <div className="mh-scforh-pillar__icon">👥</div>
                <h3>{t("mhScforhPillarSocialTitle", "Intergenerational Connection")}</h3>
                <p>{t("mhScforhPillarSocialBody")}</p>
              </article>
            </div>

            {/* 7 SCforH Principles */}
            <div className="mh-scforh-principles">
              <h3>{t("mhScforh7Title", "7 SCforH Guiding Principles Integrated into SmartTTClub")}</h3>
              <div className="mh-scforh-principles__grid">
                {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                  <span key={num} className="mh-scforh-tag">
                    {t(`mhScforhP${num}`)}
                  </span>
                ))}
              </div>
            </div>

            <p className="mh-note">{t("mhMethodResearch")}</p>
            <p className="mh-note">
              <Trans
                i18nKey="mhMethodMore"
                components={{
                  link: (
                    <a href={SCFORH_INFO_URL} target="_blank" rel="noopener noreferrer">
                      scforh.info
                    </a>
                  ),
                }}
              />
            </p>

            {/* SMART 4-Week Cycle */}
            <h3 style={{ marginTop: 48, fontSize: 24 }}>{t("mhMethodSmartTitle", "The SmartTTClub 4-Week SMART Cycle")}</h3>

            {/* SmartTTClub Breakdown: SMART + TT + Club (One Health) */}
            <div className="mh-brand-meaning">
              <div className="mh-brand-meaning__card">
                <span className="mh-brand-meaning__tag">SMART</span>
                <h4>{t("mhSmartMeaningTitle", "SMART Approach & AI")}</h4>
                <p>{t("mhSmartMeaningBody", "Goal-oriented progression and intelligent coaching assistance powered by Pingflo.")}</p>
              </div>
              <div className="mh-brand-meaning__card">
                <span className="mh-brand-meaning__tag">TT</span>
                <h4>{t("mhTTMeaningTitle", "Training Tool (TT)")}</h4>
                <p>{t("mhTTMeaningBody", "Engineered specifically for table tennis live hall orchestration, rotations, and multi-table training.")}</p>
              </div>
              <div className="mh-brand-meaning__card mh-brand-meaning__card--onehealth">
                <span className="mh-brand-meaning__tag mh-brand-meaning__tag--onehealth">Club</span>
                <h4>{t("mhClubMeaningTitle", "The Club & ONE Health")}</h4>
                <p>{t("mhClubMeaningBody", "Targeting the whole community around the club to become conscious that we have ONE Health. Sport clubs are the ultimate place to realize the impact of your preferred sport on your health and understand how to preserve it.")}</p>
              </div>
            </div>

            <div className="mh-smart">
              {["S", "M", "A", "R", "T"].map((letter) => (
                <article key={letter}>
                  <h3>
                    {letter} — {t(`mhSmart${letter}Title`)}
                  </h3>
                  <p>{t(`mhSmart${letter}Body`)}</p>
                </article>
              ))}
            </div>
            <p className="mh-note">{t("mhMethodMtNote")}</p>
          </div>
        </section>

        {/* PILOT INQUIRY SECTION */}
        <section className="mh-section" id="pilot">
          <div className="mh-wrap mh-pilot-split">
            <div>
              <p className="mh-eyebrow">{t("mhPilotEyebrow")}</p>
              <h2>{t("mhPilotTitle")}</h2>
              <p className="mh-lead">{t("mhPilotWhat")}</p>
              <dl className="mh-dl">
                <dt>{t("mhPilotInTitle")}</dt>
                <dd>{t("mhPilotInBody")}</dd>
                <dt>{t("mhPilotOutTitle")}</dt>
                <dd>{t("mhPilotOutBody")}</dd>
                <dt>{t("mhPilotAskTitle")}</dt>
                <dd>{t("mhPilotAskBody")}</dd>
                <dt>{t("mhPilotGetTitle")}</dt>
                <dd>{t("mhPilotGetBody")}</dd>
              </dl>
            </div>
            <PilotInquiryForm />
          </div>
        </section>

        {/* TRUST & PRIVACY */}
        <section className="mh-section" id="trust" style={{ paddingTop: 0 }}>
          <div className="mh-wrap">
            <p className="mh-eyebrow">{t("mhTrustEyebrow")}</p>
            <h2>{t("mhTrustTitle")}</h2>
            <div className="mh-cards-3">
              <article className="mh-card">
                <h3>{t("mhTrustGdprTitle")}</h3>
                <p>{t("mhTrustGdpr")}</p>
              </article>
              <article className="mh-card">
                <h3>{t("mhTrustMinorsTitle")}</h3>
                <p>{t("mhTrustMinors")}</p>
              </article>
              <article className="mh-card">
                <h3>{t("mhTrustAiTitle")}</h3>
                <p>{t("mhTrustAi")}</p>
              </article>
            </div>
            <p className="mh-note">{t("mhTrustPingwe")}</p>
          </div>
        </section>

        {/* FAQ */}
        <section className="mh-section" id="faq" style={{ paddingTop: 0 }}>
          <div className="mh-wrap mh-faq">
            <p className="mh-eyebrow">{t("mhFaqEyebrow")}</p>
            <h2>{t("mhFaqTitle")}</h2>
            {[1, 2, 3, 4, 5, 6, 7].map((n) => (
              <details key={n}>
                <summary>{t(`mhFaq${n}Q`)}</summary>
                <p>{t(`mhFaq${n}A`)}</p>
              </details>
            ))}
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="mh-wrap mh-cta">
          <h2>{t("mhFinalTitle")}</h2>
          <p className="mh-lead">{t("mhFinalBody")}</p>
          <MarketingHashLink hashId="pilot" className="mh-btn mh-btn--primary">
            {t("mhRequestPilot")}
          </MarketingHashLink>
        </section>

        {/* MOBILE FLOATING ACTION BAR */}
        <div className="mh-mobile-sticky-bar" aria-hidden="true">
          <div className="mh-mobile-sticky-bar__inner">
            <span className="mh-mobile-sticky-bar__text">Cohort Zero Pilot</span>
            <MarketingHashLink hashId="pilot" className="mh-btn mh-btn--primary mh-btn--sm">
              {t("mhRequestPilot")}
            </MarketingHashLink>
          </div>
        </div>
      </main>
    </MarketingChrome>
  );
}
